import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Info } from "lucide-react";
import { siteData } from "@/lib/aht/pageData";
import { validateProposal } from "@/data/validate";
import { imageUrl } from "@/components/aht/Img";
import { readRoute } from "@/App";

const imageExists = (path) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img.naturalWidth > 0);
    img.onerror = () => resolve(false);
    img.src = imageUrl(path);
  });

// Draft checklist (status "draft") or the on-screen report (/<code>?check=1): the same validation
// the build runs, shown at the top of the page.
export function CheckPanel({ proposal, mode = "draft" }) {
  const { ui } = siteData;
  const s = ui.status;
  const [result, setResult] = useState(null);
  useEffect(() => {
    let on = true;
    validateProposal(proposal, { imageExists, expectedCode: readRoute().code }).then((r) => on && setResult(r));
    return () => {
      on = false;
    };
  }, [proposal]);

  const title = mode === "report" ? s.checkReportTitle : s.checklistTitle;
  return (
    <section className="wrap pt-6" aria-labelledby="check-title" data-testid="check-panel">
      <div className="rounded-tile bg-mist p-6 md:p-8" style={{ boxShadow: "inset 0 0 0 1px var(--hairline)" }}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="check-title" className="t-tile">
            {title} <span className="text-graphite">({proposal.meta.code}, {proposal.meta.status})</span>
          </h2>
          {mode === "draft" && <p className="t-caption text-graphite">{s.checklistIntro}</p>}
        </div>
        {!result && <p className="t-body mt-4 text-graphite">Checking…</p>}
        {result && !result.errors.length && !result.warnings.length && (
          <p className="t-body mt-4 inline-flex items-center gap-2" data-testid="check-clear">
            <CheckCircle2 size={20} strokeWidth={1.75} aria-hidden="true" className="text-accent-text" />
            {s.checklistClear}
          </p>
        )}
        {result && result.errors.length > 0 && (
          <div className="mt-5">
            <h3 className="t-ui font-semibold">{s.errorsLabel} ({result.errors.length})</h3>
            <ul className="mt-2 space-y-2" data-testid="check-errors">
              {result.errors.map((m) => (
                <li key={m} className="t-body flex items-start gap-2.5">
                  <AlertCircle size={18} strokeWidth={1.75} aria-hidden="true" className="mt-1 shrink-0 text-ink" />
                  {m}
                </li>
              ))}
            </ul>
          </div>
        )}
        {result && result.warnings.length > 0 && (
          <div className="mt-5">
            <h3 className="t-ui font-semibold">{s.warningsLabel} ({result.warnings.length})</h3>
            <ul className="mt-2 space-y-2" data-testid="check-warnings">
              {result.warnings.map((m) => (
                <li key={m} className="t-body flex items-start gap-2.5 text-graphite">
                  <Info size={18} strokeWidth={1.75} aria-hidden="true" className="mt-1 shrink-0" />
                  {m}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
